import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
    ComposedChart,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Bar,
    Customized,
    Label,
    Legend
} from "recharts";
import { StatusLegend } from "./SalesAIChart";


const stageGroups = [
    { name: "Product Marketing", color: "rgba(168, 126, 177, 0.18)", range: [0, 8] },
    { name: "Communication Marketing", color: "rgba(129, 221, 178, 0.27)", range: [8, 11] },
    { name: "IT, Security and Risk", color: "rgba(250, 227, 195, 0.42)", range: [11, 15] }
];

const statusColors = {
    "completed": "#4CAF50",
    "in-process": "#FFC107",
    "stopped": "#F44336",
    "NA": "#9E9E9E"
};

const MarketPlanAIChart = ({ data, stages }) => {
    const rowHeight = 200;
    const whiteBox = 115;


    // Transform for Recharts
    const transformedData = () => {
        const result = []
        data.forEach((group) => {
            group.sub_tasks.forEach((sub, index) => {
                const row = { task: sub.task, mainTask: index === 0 ? group.mainTask : "" };
                stages.forEach((stage, i) => {
                    if (sub[stage]) {
                        row[`stage_${i}`] = 1;
                        row[`meta_${i}`] = { ...sub[stage], stage };
                    } else {
                        row[`stage_${i}`] = 0;
                    }
                });
                result.push(row);
            });
        });
        return result;
    }

    // Custom Y-Axis Tick with main task label
    const CustomYAxisTick = ({ x, y, payload, index }) => {
        const row = transformedData[index];
        const tooltipResult = (
            <Tooltip className='agent-tooltip'>
                {payload.value}
            </Tooltip>
        );
        return (
            <OverlayTrigger placement="right" overlay={tooltipResult}>
                <g transform={`translate(${x},${y})`}>
                    <text x={0} y={-10} dy={5} fontSize={12} textAnchor="middle" transform="rotate(-90)">
                        {payload.value.length > 20 ? `${payload.value.slice(0, 20)}...` : payload.value}
                    </text>
                </g>
            </OverlayTrigger>
        );
    };

    const WrappedXAxisTick = ({ x, y, payload, index }) => {
        const stageIndex = Math.floor(payload.value); // get index
        const label = stages[stageIndex] || "";

        const words = label.replaceAll("_", " ").split(" ");
        const mid = Math.ceil(words.length / 2);
        const line1 = words.slice(0, mid).join(" ");
        const line2 = words.slice(mid).join(" ");

        return (
            <g transform={`translate(${x},${y})`}>
                <text textAnchor="middle" fill="#666" fontSize={10}>
                    <tspan x={0} dy={-12}>{line1.charAt(0).toUpperCase() + line1.slice(1).toLowerCase()}</tspan>
                    <tspan x={0} dy={12}>{line2.charAt(0).toUpperCase() + line2.slice(1).toLowerCase()}</tspan>
                </text>
            </g>
        );
    };
    return (
        <ResponsiveContainer width="100%" height={transformedData().length * (rowHeight) + 100}>
            <ComposedChart
                layout="vertical"
                data={transformedData()}
                margin={{ top: 40, right: 50, left: 50, bottom: 20 }}
            >
                {/* Grouped background blocks */}
                <Customized
                    component={({ height }) => {
                        let y = 0;
                        return transformedData().map((row, index) => {
                            if (row.mainTask) {
                                const group = transformedData().find(g => g.mainTask === row.mainTask);
                                const blockHeight = group.sub_tasks.length * rowHeight;
                                const fill = index % 2 === 0 ? "#f0f0ff" : "#f9f9f9";
                                const el = (
                                    <rect
                                        key={index}
                                        x={0}
                                        y={y}
                                        width="100%"
                                        height={blockHeight}
                                        fill='transparent'
                                        opacity={0.4}
                                    />
                                );
                                y += blockHeight;
                                return el;
                            }
                            return null;
                        });
                    }}
                />
                <Customized
                    component={({ xAxisMap, height }) => {
                        const xAxis = xAxisMap[0];
                        return stageGroups.map((group, idx) => {
                            const x = xAxis.scale(group.range[1]); // end of group
                            return (
                                <line
                                    key={`separator-${idx}`}
                                    x1={x}
                                    x2={x}
                                    y1={0}
                                    y2={height}
                                    stroke="#ccc"
                                    strokeWidth={1}
                                    strokeDasharray="4 2" // dashed line
                                />
                            );
                        });
                    }}
                />
                <Legend verticalAlign="bottom" content={StatusLegend} />

                {/* Stage group background headers */}
                <Customized
                    component={({ xAxisMap, height }) => {
                        const xAxis = xAxisMap[0];
                        return stageGroups.map((group, idx) => {
                            const xStart = xAxis.scale(group.range[0]);
                            const xEnd = xAxis.scale(group.range[1]);
                            const rectX = xStart;
                            const rectWidth = xEnd - xStart;
                            const labelX = xStart + rectWidth / 2;
                            return (
                                <g key={idx}>
                                    <rect
                                        x={rectX}
                                        y={0}
                                        width={rectWidth}
                                        height={height}
                                        fill={group.color}
                                        opacity={0.3}
                                    />
                                    <text
                                        x={labelX}
                                        y={20}
                                        textAnchor="middle"
                                        fontSize={12}
                                        fill="#333"
                                        fontWeight="600"
                                    >
                                        {group.name}
                                    </text>
                                </g>
                            );
                        });
                    }}
                />

                <XAxis
                    type="number"
                    orientation="top"
                    domain={[0, stages.length]}
                    ticks={stages.map((_, i) => i + 0.5)}
                    tick={<WrappedXAxisTick stages={stages} />}
                    interval={0}
                />
                <YAxis
                    type="category"
                    dataKey="task"
                    tick={<CustomYAxisTick />}
                    width={50}
                >
                    <Label value="Initiates"
                        angle={-90}
                        position={{ x: 0, y: '50%' }}
                        textAnchor="start"
                        style={{ textAnchor: 'middle', fill: '#333', fontSize: 16, fontWeight: 'bold' }} />
                </YAxis>

                {/* Bars */}
                {stages.map((stage, i) => (
                    <Bar
                        key={stage}
                        dataKey={`stage_${i}`}
                        stackId="a"
                        barSize={30}
                        shape={(props) => {
                            const { x, y, height, payload } = props;
                            const meta = payload[`meta_${i}`];
                            if (!meta || !meta.status) return null;
                            const tooltipResult = (
                                <Tooltip className='agent-tooltip'>
                                    {meta.assignee}
                                </Tooltip>
                            );
                            return (
                                <g>
                                    <rect
                                        x={x}
                                        y={y}
                                        width={whiteBox}
                                        height={height}
                                        fill={statusColors[meta.status]}
                                    />
                                    {/* <rect
                                        x={x + whiteBox}
                                        y={y}
                                        width={20}
                                        height={height}
                                        fill={statusColors[meta.status] || "#ccc"}
                                    /> */}
                                    {meta.assignee && (
                                        <OverlayTrigger placement="right" overlay={tooltipResult}>
                                            <g>
                                                <rect
                                                    x={x + whiteBox + 10}
                                                    y={y}
                                                    rx={15}
                                                    width={30}
                                                    height={height}
                                                    fill='gray'
                                                />

                                                <text
                                                    x={x + whiteBox + 25}
                                                    y={y + height / 2 + 5}
                                                    textAnchor="middle"
                                                    fontSize={16}
                                                    fill="white"
                                                >
                                                    {meta.assignee[0]}
                                                </text>
                                            </g>
                                        </OverlayTrigger>
                                    )}
                                </g>
                            );
                        }}
                    />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default MarketPlanAIChart;
